"use client"

import { useState, useEffect } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Radio,
} from "@mui/material"
import { Close as CloseIcon } from "@mui/icons-material"
import { useDispatch, useSelector } from "react-redux"
import { updateUser } from "../../slices/userSlice"
import { toast } from "react-toastify"
import axios from "axios"
import { API_URL } from "../../constants"

const UplinkDialog = ({ open, onClose, user }) => {
  const dispatch = useDispatch()
  const { userInfo } = useSelector((state) => state.auth)
  const { loading } = useSelector((state) => state.users)
  const [selectedParent, setSelectedParent] = useState(null)
  const [allResellers, setAllResellers] = useState([]) // New state for all resellers
  const [loadingResellers, setLoadingResellers] = useState(false) // Loading state for resellers
  const [parentChain, setParentChain] = useState([])
  const [descendantIds, setDescendantIds] = useState([])

  // Initialize selectedParent when user prop changes
  useEffect(() => {
    if (user) {
      setSelectedParent(user.parentId || "")
    }
  }, [user])

  // Fetch all resellers when dialog opens
  useEffect(() => {
    const fetchAllResellers = async () => {
      if (!open || userInfo.user.role !== "admin") {
        setAllResellers([])
        return
      }

      setLoadingResellers(true)
      try {
        const config = {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
        }
        const { data } = await axios.get(`${API_URL}/api/users/resellers/all`, config)
        setAllResellers(data)
      } catch (error) {
        console.error("Failed to fetch all resellers:", error)
        toast.error("Failed to fetch resellers.")
        setAllResellers([])
      } finally {
        setLoadingResellers(false)
      }
    }

    fetchAllResellers()
  }, [open, userInfo.token, userInfo.user.role])

  // Fetch parent chain and descendants only if the current user is an admin and the dialog is open
  useEffect(() => {
    const fetchParentChainAndDescendants = async () => {
      if (!user || userInfo.user.role !== "admin" || !open) {
        setParentChain([])
        setDescendantIds([])
        return
      }

      try {
        const config = {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
        }
        // Fetch parent chain
        const { data: parentChainData } = await axios.get(`${API_URL}/api/users/${user._id}/parent-chain`, config)
        setParentChain(parentChainData)

        // Fetch descendants if the user is a reseller
        if (user.role === "reseller") {
          const { data: descendantsData } = await axios.get(`${API_URL}/api/users/${user._id}/descendants`, config)
          setDescendantIds(descendantsData)
        } else {
          setDescendantIds([])
        }
      } catch (error) {
        console.error("Failed to fetch parent chain or descendants:", error)
        toast.error("Failed to fetch uplink data.")
        setParentChain([])
        setDescendantIds([])
      }
    }

    fetchParentChainAndDescendants()
  }, [user, userInfo.token, userInfo.user.role, open])

  // Filter and order resellers based on parent chain and exclude descendants
  const getFilteredAndOrderedResellers = () => {
    if (!user || !allResellers.length) return []

    // Filter for resellers, excluding the user being edited, the current admin, and any of the user's descendants
    const filteredResellers = allResellers.filter(
      (reseller) =>
        reseller._id !== user._id && // Exclude the user being edited
        reseller._id !== userInfo.user._id && // Exclude the current admin
        !descendantIds.includes(reseller._id), // Exclude any of the user's descendants
    )

    // If parentChain is available (i.e., admin is logged in), order based on it
    if (userInfo.user.role === "admin" && parentChain.length > 0) {
      const ordered = [...filteredResellers].sort((a, b) => {
        const aIndex = parentChain.findIndex((u) => u.userId === a._id)
        const bIndex = parentChain.findIndex((u) => u.userId === b._id)

        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex
        } else if (aIndex !== -1) {
          return -1
        } else if (bIndex !== -1) {
          return 1
        } else {
          return 0
        }
      })
      return ordered
    } else {
      // If not admin or no parent chain, just return filtered resellers
      return filteredResellers
    }
  }

  const orderedResellers = getFilteredAndOrderedResellers()

  const handleUpdateUplink = async () => {
    const newParentId = selectedParent === "" ? null : selectedParent

    if (newParentId === user.parentId) {
      toast.info("No change in uplink detected.")
      onClose()
      return
    }

    try {
      await dispatch(
        updateUser({
          id: user._id,
          userData: { parentId: newParentId },
        }),
      ).unwrap()
      toast.success("Uplink updated successfully.")
      onClose()
    } catch (error) {
      console.error("Failed to update uplink:", error)
      toast.error("Failed to update uplink.")
    }
  }

  const handleParentSelectionChange = (event) => {
    setSelectedParent(event.target.value)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Update Uplink for: {user?.name} ({user?.role.toUpperCase()})
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loadingResellers ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "20px" }}>
            <CircularProgress />
          </div>
        ) : (
          <TableContainer component={Paper}>
            <Table aria-label="uplink table">
              <TableHead>
                <TableRow>
                  <TableCell>Select</TableCell>
                  <TableCell>SR. NO.</TableCell>
                  <TableCell>NAME</TableCell>
                  <TableCell>USER ID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Option to set current Admin as parent */}
                {userInfo.user.role === "admin" && (
                  <TableRow>
                    <TableCell>
                      <Radio
                        checked={selectedParent === ""}
                        onChange={handleParentSelectionChange}
                        value=""
                        name="parent-radio-buttons"
                      />
                    </TableCell>
                    <TableCell>0</TableCell>
                    <TableCell>{userInfo.user.name} (Admin)</TableCell>
                    <TableCell>{userInfo.user.email}</TableCell>
                  </TableRow>
                )}
                {orderedResellers && orderedResellers.length > 0 ? (
                  orderedResellers.map((parent, index) => (
                    <TableRow key={parent._id}>
                      <TableCell>
                        <Radio
                          checked={selectedParent === parent._id}
                          onChange={handleParentSelectionChange}
                          value={parent._id}
                          name="parent-radio-buttons"
                        />
                      </TableCell>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{parent.name}</TableCell>
                      <TableCell>{parent.email}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No resellers available to set as uplink.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Button
          onClick={handleUpdateUplink}
          variant="contained"
          fullWidth
          sx={{ mt: 2, bgcolor: "#008069", "&:hover": { bgcolor: "#128C7E" } }}
          disabled={loading || selectedParent === null || loadingResellers}
        >
          {loading ? <CircularProgress size={24} /> : "Update Uplink"}
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ color: "text.secondary" }}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default UplinkDialog
